import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { useSaveScreeningResultsMutation } from '@/entities/screening/api'
import { screeningFormSchema } from '@/entities/screening/model/screening.schema'
import type { ScreeningFormValues } from '@/entities/screening/model/screening.schema'
import { toaster } from '@/shared/ui'
import { getAxiosErrorData } from '@/shared/utils'
import type {
  IApiError,
  IApiErrorField,
  IApiResponse,
} from '@/shared/types/api'

/** No lab panel is configured for the donor's organization/category/donation type. */
const LAB_CONFIG_MISSING_CODE = '500110'

export function useScreeningForm(visitId: string, donorId: string) {
  const { t } = useTranslation()
  const schema = useMemo(() => screeningFormSchema(t), [t])
  // Admin-side misconfiguration the nurse can't fix — shown as a blocking
  // dialog rather than a toast so it isn't missed.
  const [labConfigError, setLabConfigError] = useState<string | null>(null)

  const form = useForm<ScreeningFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      hemoglobin: '',
      blood_group_id: '',
      rh_factor_id: '',
    },
  })

  const { mutateAsync, isPending } = useSaveScreeningResultsMutation(
    visitId,
    donorId,
  )

  const onSubmit = form.handleSubmit(async (data) => {
    try {
      const response = await mutateAsync({
        hemoglobin: Number(data.hemoglobin),
        blood_group_id: data.blood_group_id,
        rh_factor_id: data.rh_factor_id,
      })
      if (response.data.result === 'DEFERRED') {
        toaster.error({
          title: response.data.message,
          duration: 5000,
        })
      } else {
        toaster.success({
          title:
            response.data.message || t('donor_detail.screening.save_success'),
          duration: 5000,
        })
      }

      form.reset()
    } catch (error) {
      const body = getAxiosErrorData<IApiResponse<null>>(error)
      if (body?.errors[0]?.message) {
        const labConfigMissing = body.errors.find(
          (apiError) => apiError.code === LAB_CONFIG_MISSING_CODE,
        )
        if (labConfigMissing) {
          setLabConfigError(labConfigMissing.message)
          return
        }
        body.errors.forEach((apiError: IApiError) => {
          if (apiError.code === '200000') {
            apiError.fields.forEach((field: IApiErrorField) => {
              form.setError(field.key as keyof ScreeningFormValues & string, {
                message: field.value,
              })
            })
          } else {
            toaster.error({ title: apiError.message })
          }
        })
        return
      }
      toaster.error({ title: t('donor_detail.screening.save_error') })
    }
  })

  return {
    form,
    isPending,
    onSubmit,
    labConfigError,
    closeLabConfigError: () => setLabConfigError(null),
  }
}
